import { useId, type ReactNode } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/cn";

interface Props {
  /** Dialog heading — «Новый заказ». Truncated with a `title` so a long one cannot push ✕ off. */
  title: string;
  /** Muted line beside the title — «23 авг. 2026 · Demo Delivery Co.» */
  subtitle?: ReactNode;
  /** Extra header content between the subtitle and the ✕ (chips, action buttons). */
  headerActions?: ReactNode;
  /** Exactly three `ModalColumn`s in source order: they stack in this order at 375. */
  children: ReactNode;
  /** Left-aligned footer text — the «Обязательны адрес и дата …» hint. */
  footerHint?: ReactNode;
  /** Right-aligned footer controls. */
  footerActions?: ReactNode;
  /** Full-width banner between header and columns (root-level form errors). */
  banner?: ReactNode;
  /** Wraps the columns; the create modals pass their `<form>` so the footer submit works. */
  bodyWrapper?: (columns: ReactNode) => ReactNode;
  /**
   * Widen the middle column at the expense of the third — the order DETAIL view's
   * proportions (owner, 2026-08-25: «3-я колонка должна быть гораздо уже, смотри
   * макет, тогда у нас центральная будет шире и там всё поместится как на макете»).
   *
   * Opt-in, never the default: the create modal and both template modals are three
   * FORMS of comparable density, and equal thirds are correct there. Only the detail
   * VIEW is asymmetric — its middle column carries ДОСТАВКА + ГРУЗ + КЛИЕНТ + notes +
   * ПОСЫЛКИ while ЖУРНАЛ is a short timeline, so equal thirds starved the middle one
   * and left «Без подтверждения» wrapping onto two lines at every width.
   *
   * The ratio is MEASURED from the two mockups, not chosen for roundness. Decoding
   * both PNGs and locating the two full-height divider rules gives, as a fraction of
   * the dialog's interior width:
   *
   *   parcels ON  (1366px shot): 408 / 626 / 312 px → 0.3031 / 0.4651 / 0.2318
   *   parcels OFF (1432px shot): 425 / 653 / 326 px → 0.3027 / 0.4651 / 0.2322
   *
   * The two agree to within 0.05%, and `1.3fr 2fr 1fr` reproduces them to within
   * 0.06% (0.3023 / 0.4651 / 0.2326) — so that is the ratio, expressed as the
   * mockup's own proportions rather than as transcribed pixel widths that would only
   * be right at one dialog width.
   */
  wideMiddle?: boolean;
  className?: string;
}

/**
 * The three-column dialog shell shared by all five Sprint 186 order surfaces.
 *
 * This component owns the two layout guarantees that no unit test can see, so
 * they are enforced once here instead of being re-derived (and re-broken) on
 * each surface:
 *
 * 1. **No internal scrolling at ≥1280.** The body is a `lg:grid-cols-3` grid
 *    that is `lg:overflow-visible` — only below `lg` does it become a scroll
 *    container. The height budget comes from `max-h-[95dvh]`; `dvh` rather than
 *    `vh` because mobile browser chrome makes `vh` overshoot and pushes the
 *    footer off-screen. 95 rather than the 92 the brief estimated: measured at
 *    1280×800 the tallest realistic form needed 601 px against 596 available,
 *    and buying the last 5 px here is honest, where shaving a fifth control
 *    would have been cosmetic damage to hit a number.
 * 2. **No sideways page scroll.** `minmax(0,1fr)` on every track plus `min-w-0`
 *    on every column (see {@link ModalColumn}). Without both, one long unbroken
 *    address widens its grid track past its share and the whole dialog scrolls
 *    horizontally — the single most common cause of that criterion failing.
 *
 * At 375 the dialog is a bottom-anchored sheet with a `sticky` footer, so the
 * primary action can never be the thing that lands off-screen.
 */
export function ModalThreeColumn({
  title,
  subtitle,
  headerActions,
  children,
  footerHint,
  footerActions,
  banner,
  bodyWrapper,
  wideMiddle = false,
  className,
}: Props) {
  const { t } = useTranslation();
  /*
    The footer hint doubles as the dialog's accessible description. Radix warns
    when a DialogContent has neither a Description nor `aria-describedby`, and
    the hint («Обязательны адрес и дата …») is exactly the sentence a
    screen-reader user needs on open — so it is wired up rather than silenced.
  */
  const descriptionId = useId();

  const columns = (
    <div
      className={cn(
        "grid min-h-0 flex-1 grid-cols-1 divide-y divide-gray-100 overflow-y-auto",
        "lg:divide-x lg:divide-y-0 lg:overflow-visible",
        // Both variants keep `minmax(0,…)` on every track: without the 0 minimum a
        // grid item's automatic minimum size refuses to shrink below its content,
        // and one long unbroken address widens its track past its share and scrolls
        // the whole dialog sideways. The ratio changes; that guarantee does not.
        //
        // Static class strings, never an interpolated `lg:grid-cols-[${…}]` —
        // Tailwind scans source text, so a computed name is never generated and the
        // rule silently does not exist (the same trap documented on `ModalColumn`'s
        // `order` prop).
        wideMiddle
          ? "lg:grid-cols-[minmax(0,1.3fr)_minmax(0,2fr)_minmax(0,1fr)]"
          : "lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)]",
      )}
    >
      {children}
    </div>
  );

  return (
    <Dialog.Portal>
      <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm" />
      <Dialog.Content
        aria-describedby={footerHint ? descriptionId : undefined}
        className={cn(
          "fixed inset-x-0 bottom-0 top-auto z-50 flex max-h-[95dvh] w-full flex-col overflow-hidden",
          "rounded-t-2xl bg-white shadow-xl focus:outline-none",
          "sm:bottom-auto sm:left-1/2 sm:top-1/2 sm:max-w-[1160px] sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-2xl",
          "xl:max-w-[1320px]",
          className,
        )}
      >
        <div className="flex shrink-0 items-center gap-3 border-b border-gray-100 px-5 py-4 sm:px-6">
          <div className="flex min-w-0 flex-1 items-baseline gap-3">
            <Dialog.Title
              className="truncate text-lg font-bold text-gray-900"
              title={title}
            >
              {title}
            </Dialog.Title>
            {subtitle && (
              <span className="hidden truncate text-sm text-gray-400 sm:block">
                {subtitle}
              </span>
            )}
          </div>
          {headerActions && (
            <div className="flex shrink-0 items-center gap-2">
              {headerActions}
              {/*
                TASK-4836 — the mockup's hairline between the actions and «✕».
                «Закрыть» dismisses the dialog; everything left of it acts on the
                record. The rule says so without a word of copy. Decorative, so
                aria-hidden — a separator in the a11y tree would be noise.
              */}
              <span aria-hidden="true" className="ml-1 h-6 w-px bg-gray-200" />
            </div>
          )}
          <Dialog.Close asChild>
            <button
              type="button"
              /*
                «Закрыть окно», not «Закрыть»: the template VIEW's footer now
                carries a «Закрыть» button too, and two controls in one dialog
                with the identical accessible name is the BUG-504/508 family —
                a screen-reader user hears "Закрыть, button" twice with no way
                to tell the ✕ from the footer action. The visible ✕ has no text
                label, so naming it more specifically costs nothing on screen.
              */
              aria-label={t("common.closeDialog")}
              // TASK-4836 — h-10/w-10 to sit on the same baseline as the header
              // actions beside it; `p-1.5` rendered a 32px box next to 40px buttons.
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
            >
              <X className="h-5 w-5" aria-hidden="true" />
            </button>
          </Dialog.Close>
        </div>

        {banner && <div className="shrink-0 px-5 pt-4 sm:px-6">{banner}</div>}

        {bodyWrapper ? bodyWrapper(columns) : columns}

        {(footerHint || footerActions) && (
          <div className="sticky bottom-0 flex shrink-0 flex-col gap-3 border-t border-gray-100 bg-white px-5 py-4 sm:flex-row sm:items-center sm:px-6">
            {footerHint && (
              <p
                id={descriptionId}
                className="min-w-0 flex-1 text-xs text-gray-400"
              >
                {footerHint}
              </p>
            )}
            {footerActions && (
              // `sm:ml-auto` (owner, 2026-08-24): the actions were pushed right
              // only by the hint's `flex-1` beside them, so a footer WITHOUT a
              // hint — the order-detail modal in edit mode — left «Отмена» /
              // «Сохранить» sitting against the left edge. Measured before the
              // fix: x=120 and x=202 in a 1320px dialog. `ml-auto` makes the
              // group right-align on its own, hint or no hint.
              <div className="flex flex-wrap items-center justify-end gap-2 sm:ml-auto sm:shrink-0">
                {footerActions}
              </div>
            )}
          </div>
        )}
      </Dialog.Content>
    </Dialog.Portal>
  );
}
